import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  joinUrlForCurrentOrigin,
  micPairingService,
  type MicPairingCreated,
} from '../services/micPairingService';
import { useRemoteMicSession, type RemoteMicSession } from '../hooks/useRemoteMicSession';
import type { AudioFinalMeta } from '../../pages/MicPage/micProtocol';

export type RemoteMicAudioHandler = (blob: Blob, meta: AudioFinalMeta) => void;

interface MicSessionContextValue {
  pairing: MicPairingCreated | null;
  qrDataUrl: string | null;
  pairingLoading: boolean;
  pairingError: boolean;
  session: RemoteMicSession;
  /** Vive aquí y no en la UI del editor para que el panel siga abierto tras remontar la página. */
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  /** Emite un pairing nuevo y conecta el escritorio al relay. */
  startPairing: (draftId?: string) => void;
  stopPairing: () => void;
  /** DocumentCanvas registra aquí el sumidero del audio que llega del móvil. */
  setAudioHandler: (handler: RemoteMicAudioHandler | null) => void;
}

const MicSessionCtx = createContext<MicSessionContextValue | null>(null);

// El provider vive montado en toda la app; `uqr` solo hace falta cuando se pide
// un emparejamiento, así que se carga bajo demanda y no lastra el bundle inicial.
async function qrFromJoinUrl(joinUrl: string): Promise<string> {
  const { renderSVG } = await import('uqr');
  const svg = renderSVG(joinUrl, {
    border: 1,
    ecc: 'M',
    pixelSize: 8,
    whiteColor: 'transparent',
    blackColor: '#111111',
  });
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function MicSessionProvider({ children }: { children: ReactNode }) {
  const [pairing, setPairing] = useState<MicPairingCreated | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingError, setPairingError] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const audioHandlerRef = useRef<RemoteMicAudioHandler | null>(null);
  const mintTokenRef = useRef(0);
  const activePairingIdRef = useRef<string | null>(null);

  const onAudio = useCallback<RemoteMicAudioHandler>((blob, meta) => {
    audioHandlerRef.current?.(blob, meta);
  }, []);

  const session = useRemoteMicSession({ onAudio });
  const { connect, disconnect } = session;

  const setAudioHandler = useCallback((handler: RemoteMicAudioHandler | null) => {
    audioHandlerRef.current = handler;
  }, []);

  const stopPairing = useCallback(() => {
    mintTokenRef.current += 1;
    disconnect();
    const id = activePairingIdRef.current;
    activePairingIdRef.current = null;
    setPairing(null);
    setQrDataUrl(null);
    setPairingLoading(false);
    setPairingError(false);
    if (id) void micPairingService.revoke(id).catch(() => {});
  }, [disconnect]);

  const startPairing = useCallback((draftId?: string) => {
    const token = ++mintTokenRef.current;
    const previousId = activePairingIdRef.current;
    activePairingIdRef.current = null;
    if (previousId) void micPairingService.revoke(previousId).catch(() => {});

    disconnect();
    setPairing(null);
    setQrDataUrl(null);
    setPairingError(false);
    setPairingLoading(true);

    void (async () => {
      try {
        const created = await micPairingService.create(draftId);
        if (token !== mintTokenRef.current) {
          void micPairingService.revoke(created.pairingId).catch(() => {});
          return;
        }
        activePairingIdRef.current = created.pairingId;
        setPairing(created);
        connect(created.pairingId, created.token);
        // El QR se ancla al origen actual: un usuario de marca blanca tiene que
        // mandar su teléfono a SU dominio, no al del front principal.
        const qr = await qrFromJoinUrl(joinUrlForCurrentOrigin(created.joinUrl));
        if (token === mintTokenRef.current) setQrDataUrl(qr);
      } catch {
        if (token === mintTokenRef.current) setPairingError(true);
      } finally {
        if (token === mintTokenRef.current) setPairingLoading(false);
      }
    })();
  }, [connect, disconnect]);

  useEffect(() => () => {
    const id = activePairingIdRef.current;
    if (id) void micPairingService.revoke(id).catch(() => {});
  }, []);

  const value = useMemo<MicSessionContextValue>(() => ({
    pairing,
    qrDataUrl,
    pairingLoading,
    pairingError,
    session,
    sidebarOpen,
    setSidebarOpen,
    startPairing,
    stopPairing,
    setAudioHandler,
  }), [pairing, qrDataUrl, pairingLoading, pairingError, session, sidebarOpen, startPairing, stopPairing, setAudioHandler]);

  return <MicSessionCtx.Provider value={value}>{children}</MicSessionCtx.Provider>;
}

export function useMicSessionContext(): MicSessionContextValue {
  const ctx = useContext(MicSessionCtx);
  if (!ctx) throw new Error('useMicSessionContext must be used within MicSessionProvider');
  return ctx;
}
